import styled, { css } from "styled-components";
import { SpeakerSelect } from "./SpeakerSelect.tsx";
import { SampleVideoPlayer } from "./SampleVideoPlayer.tsx";
import type { TokenSegment, SpeakerProfile } from "../../lib/types.ts";

type SegmentRowProps = {
  segment: TokenSegment;
  speakers: number[];
  profiles: Map<number, SpeakerProfile>;
  /** El speaker actualmente asignado (puede ser el original o un override). */
  currentSpeaker: number;
  /** True si este segmento tiene un override manual aplicado. */
  hasOverride: boolean;
  /** Padding en segundos que se aplica a la reproducción (info visual). */
  paddingBefore: number;
  paddingAfter: number;
  /** Reproducción del segmento exacto. */
  isPlaying: boolean;
  progress: number;
  /** URL del video (presente solo si el meeting es MP4). */
  videoUrl?: string | null;
  onPlay: () => void;
  onChangeSpeaker: (speakerIndex: number) => void;
};

function formatMmSs(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

export function SegmentRow({
  segment,
  speakers,
  profiles,
  currentSpeaker,
  hasOverride,
  paddingBefore,
  paddingAfter,
  isPlaying,
  progress,
  videoUrl,
  onPlay,
  onChangeSpeaker,
}: SegmentRowProps) {
  const duration = segment.endTime - segment.startTime;
  const confLabel = `Confianza ${(segment.avgConfidence * 100).toFixed(0)}%`;
  const isLowConf = segment.avgConfidence < 0.5; // indicador visual de baja confianza

  // Solo mostramos el indicador de padding si efectivamente se va a aplicar.
  const hasPadding = paddingBefore > 0 || paddingAfter > 0;

  return (
    <Wrapper $modified={hasOverride}>
      <TopRow>
        <Meta>
          <Index>#{segment.index}</Index>
          <Time>
            {formatMmSs(segment.startTime)} → {formatMmSs(segment.endTime)}
          </Time>
          <Duration>({duration.toFixed(1)}s)</Duration>
          {hasPadding && (
            <PaddingHint>
              + {paddingBefore.toFixed(0)}s antes / {paddingAfter.toFixed(0)}s después
            </PaddingHint>
          )}
        </Meta>

        <Confidence $isLowConf={isLowConf}>{confLabel}</Confidence>
      </TopRow>

      <PlayRow>
        <PlayButton
          type="button"
          onClick={onPlay}
          $playing={isPlaying}
          aria-label="Reproducir segmento"
        >
          {isPlaying ? "■" : "▶"}
          {isPlaying && <PlayProgress style={{ width: `${progress * 100}%` }} />}
        </PlayButton>
        <Text>"{segment.text}"</Text>
      </PlayRow>

      {isPlaying && videoUrl && (
        <SampleVideoPlayer src={videoUrl} startTime={segment.startTime} />
      )}

      <AssignRow>
        <AssignLabel>Asignar a:</AssignLabel>
        <SpeakerSelect
          speakers={speakers}
          profiles={profiles}
          value={currentSpeaker}
          onChange={onChangeSpeaker}
        />
        {hasOverride && <ModifiedBadge>Modificado</ModifiedBadge>}
      </AssignRow>
    </Wrapper>
  );
}

const Wrapper = styled.div<{ $modified: boolean }>`
  background: ${({ theme }) => theme.color.surface};
  border: 1px solid ${({ theme }) => theme.color.border};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: ${({ theme }) => theme.space.md};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.sm};
  transition: border-color 120ms, background 120ms;

  ${({ $modified, theme }) =>
    $modified &&
    css`
      border-color: ${theme.color.success};
      background: ${theme.color.successMuted};
    `}
`;

const TopRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${({ theme }) => theme.space.sm};
  flex-wrap: wrap;
`;

const Meta = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space.sm};
  font-family: ${({ theme }) => theme.font.mono};
  font-size: ${({ theme }) => theme.font.size.xs};
  flex-wrap: wrap;
`;

const Index = styled.span`
  color: ${({ theme }) => theme.color.textDim};
`;

const Time = styled.span`
  color: ${({ theme }) => theme.color.textPrimary};
  font-weight: ${({ theme }) => theme.font.weight.medium};
`;

const Duration = styled.span`
  color: ${({ theme }) => theme.color.textDim};
`;

const PaddingHint = styled.span`
  color: ${({ theme }) => theme.color.textDim};
  font-style: italic;
`;

const Confidence = styled.span<{ $isLowConf: boolean }>`
  font-size: ${({ theme }) => theme.font.size.xs};
  font-family: ${({ theme }) => theme.font.mono};
  padding: 2px 8px;
  border-radius: ${({ theme }) => theme.radius.sm};
  background: ${({ theme, $isLowConf }) =>
    $isLowConf ? theme.color.warningMuted : theme.color.accentMuted};
  color: ${({ theme, $isLowConf }) =>
    $isLowConf ? theme.color.warning : theme.color.accent};
`;

const PlayRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.space.sm};
`;

const PlayButton = styled.button<{ $playing: boolean }>`
  position: relative;
  flex-shrink: 0;
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  border: 1px solid
    ${({ theme, $playing }) =>
      $playing ? theme.color.accent : theme.color.borderStrong};
  background: ${({ theme, $playing }) =>
    $playing ? theme.color.accent : theme.color.surface};
  color: ${({ theme, $playing }) =>
    $playing ? "white" : theme.color.textPrimary};
  cursor: pointer;
  font-size: ${({ theme }) => theme.font.size.xs};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  transition: background 120ms, color 120ms, border-color 120ms;

  &:hover {
    border-color: ${({ theme }) => theme.color.accent};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.accent};
    outline-offset: 2px;
  }
`;

const PlayProgress = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  height: 2px;
  background: white;
  transition: width 50ms linear;
`;

const Text = styled.div`
  flex: 1;
  font-size: ${({ theme }) => theme.font.size.sm};
  color: ${({ theme }) => theme.color.textPrimary};
  font-style: italic;
  line-height: 1.5;
`;

const AssignRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space.sm};
  flex-wrap: wrap;
`;

const AssignLabel = styled.span`
  font-size: ${({ theme }) => theme.font.size.sm};
  color: ${({ theme }) => theme.color.textMuted};
`;

const ModifiedBadge = styled.span`
  font-size: ${({ theme }) => theme.font.size.xs};
  font-weight: ${({ theme }) => theme.font.weight.medium};
  color: ${({ theme }) => theme.color.success};
  background: white;
  border: 1px solid ${({ theme }) => theme.color.success};
  padding: 2px 6px;
  border-radius: ${({ theme }) => theme.radius.sm};
`;
